import { card as parse, type CardId, type Rank } from '@rummy/engine';
import { forwardRef, useEffect, useState } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { PlayingCard } from './PlayingCard.tsx';

export interface CenterStackProps {
  closedCount: number;
  deckCount: number;
  discardTop: CardId | null;
  /** Cards under the top of the pile, nearest first. Public information. */
  discardBelow?: CardId[];
  wildCard: CardId;
  wildRank: Rank | null;
  canDraw: boolean;
  onDrawClosed: () => void;
  onDrawDiscard: () => void;
  /** The pile becomes a visible drop target after drawing. */
  discardMode: boolean;
}

/**
 * The centre (§6.2): the closed deck, the discard pile and the wild joker,
 * presented as three aligned objects with clear affordance — not scattered on a
 * virtual baize.
 *
 * This block never mirrors under RTL. Card order and the discard pile are a
 * left-to-right convention worldwide; mirroring them is a bug that looks like a
 * feature (§10).
 */
export const CenterStack = forwardRef<HTMLButtonElement, CenterStackProps>(
  function CenterStack(
    {
      closedCount,
      deckCount,
      discardTop,
      discardBelow = [],
      wildCard,
      wildRank,
      canDraw,
      onDrawClosed,
      onDrawDiscard,
      discardMode,
    },
    pileRef,
  ) {
    const { t } = useI18n();
    const wild = wildCard ? parse(wildCard) : null;
    const wildRankLabel = wild ? (wild.printedJoker ? (wildRank ?? 'A') : (wild.rank ?? '')) : '';
    const wildExplain = wild ? t('table.wild.explain', { rank: wildRankLabel, decks: deckCount }) : '';
    const [explainWild, setExplainWild] = useState(false);
    // A new hand cuts a new joker; the sentence about the old one goes with it.
    useEffect(() => setExplainWild(false), [wildCard]);

    return (
      <div className={`centre${canDraw ? ' centre--draw' : ''}`} dir="ltr">
        <div className="centre__slot">
          <button
            type="button"
            className={`stack stack--closed${canDraw ? ' is-live' : ''}`}
            onClick={onDrawClosed}
            disabled={!canDraw}
            aria-label={`${t('table.closed')} — ${t('table.closed.count', { count: closedCount })}`}
          >
            {/*
              A deck that visibly thins out. This drew exactly two backing
              layers whether 79 cards remained or 5 — so the one piece of
              information every player is watching for near the end of a hand
              was carried only by a number nobody was reading. The layer count
              and the offsets both fall away as the stock does.
            */}
            {Array.from({ length: closedLayers(closedCount) }, (_, i) => (
              <span
                key={i}
                className="stack__shadow"
                style={{ '--layer': i + 1 } as React.CSSProperties}
                aria-hidden="true"
              />
            ))}
            <PlayingCard id="JK:0" faceDown />
          </button>
          <p className="centre__label">
            {t('table.closed')}
            <span className="centre__count">{t('table.closed.count', { count: closedCount })}</span>
          </p>
        </div>

        <div className="centre__slot">
          <button
            type="button"
            ref={pileRef}
            className={[
              'stack stack--discard',
              canDraw ? 'is-live' : '',
              discardMode ? 'is-target' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={onDrawDiscard}
            disabled={!canDraw || discardTop === null}
            aria-label={t('table.open')}
          >
            {discardTop ? (
              <>
                {/*
                  A pile has depth. Showing a sliver of the cards under the top
                  is what a real discard pile does, and it is also the cheapest
                  table intel in the product — the last few throws, readable
                  without opening a panel. Nothing private: every one of these
                  was thrown face up in front of everybody.
                */}
                {discardBelow.map((id, i) => (
                  <span
                    key={id}
                    className="stack__buried"
                    style={{ '--depth': i + 1 } as React.CSSProperties}
                    aria-hidden="true"
                  >
                    <PlayingCard id={id} wild={isWildCard(id, wildRank)} />
                  </span>
                ))}
                <PlayingCard id={discardTop} wild={isWildCard(discardTop, wildRank)} feature />
              </>
            ) : (
              <span className="stack__empty">{t('table.open.empty')}</span>
            )}
          </button>
          <p className={`centre__label${discardMode ? ' centre__label--target' : ''}`}>
            {discardMode ? t('table.discard.here') : t('table.open')}
          </p>
        </div>

        {/*
          The joker says what it is in three words; the full sentence is a tap
          away. It used to take a whole slot plus two lines of explanation on
          every turn of every hand, for a rule a player needs to read once.
        */}
        <div className="centre__slot centre__slot--wild">
          <button
            type="button"
            className="stack stack--wild"
            onClick={() => setExplainWild((open) => !open)}
            aria-expanded={explainWild}
            aria-label={wild ? `${t('table.wild')} — ${wildExplain}` : t('table.wild')}
          >
            {wildCard ? <PlayingCard id={wildCard} wild feature /> : null}
          </button>
          <p className="centre__label">
            {wild ? t('table.wild.short', { rank: wildRankLabel }) : t('table.wild')}
          </p>
          {wild && explainWild && (
            <p className="centre__wild-note" role="status">
              {wildExplain}
            </p>
          )}
        </div>
      </div>
    );
  },
);

/**
 * Backing layers under the top of the closed deck.
 *
 * Not linear: the difference between 79 and 60 cards does not matter to anyone,
 * while the difference between 8 and 3 decides whether to keep drawing. A few
 * thresholds near the bottom carry the meaning, and the stack simply looks
 * "full" above them.
 */
function closedLayers(count: number): number {
  if (count <= 0) return 0;
  if (count <= 3) return 0;
  if (count <= 8) return 1;
  if (count <= 20) return 2;
  if (count <= 45) return 3;
  return 4;
}

function isWildCard(id: CardId, wildRank: Rank | null): boolean {
  const c = parse(id);
  return c.printedJoker || (wildRank !== null && c.rank === wildRank);
}
